import React, { useEffect } from 'react';
import {
  X, Crown, Check, Shield, Clock,
  Users, Calendar, BarChart3
} from 'lucide-react';

// Features list for sales reps
const SALES_REP_FEATURES = [
  'Unlimited meeting requests',
  'Real-time availability calendar',
  'Smart scheduling suggestions',
  'Provider contact information',
  'Meeting history & analytics',
  'Email notifications',
  'Mobile-friendly interface',
  'Compliance tracking'
];

const PaywallPopup = ({ currentUser, onSubscribe, onClose, allowClose = false }) => {
  // Load Stripe Pricing Table script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Healthcare providers don't need paywall
  if (currentUser?.user_type === 'customer') {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-xl">
          {allowClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          )}

          <div className="flex items-center space-x-3 mb-4">
            <Crown className="w-8 h-8 text-yellow-300" />
            <h2 className="text-2xl font-bold">Start Your Free Trial</h2>
          </div>

          <p className="text-blue-100">
            Try RepConnect free for 14 days. Get instant access to all features and start scheduling meetings with healthcare providers today.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Value Props */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-800">Connect</h5>
              <p className="text-sm text-gray-600">Access thousands of healthcare providers</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Calendar className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-800">Schedule</h5>
              <p className="text-sm text-gray-600">Book meetings instantly</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <BarChart3 className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h5 className="font-medium text-gray-800">Track</h5>
              <p className="text-sm text-gray-600">Monitor your success</p>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-4 text-center">Everything you need to succeed:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SALES_REP_FEATURES.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trial Badge */}
          <div className="text-center mb-4">
            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
              🎉 14 Days Free Trial - No charge today
            </div>
          </div>

          {/* Stripe Pricing Table */}
          <div className="bg-gray-50 rounded-lg p-4">
            <stripe-pricing-table
              pricing-table-id="prctbl_1ScrJzGThCZOasDrsnD8vqBY"
              publishable-key="pk_live_51SJ4xBGThCZOasDrzzExx42GogW4KwxEZyqhqTmOrAVtD0izPolQHfgPwt5QTLWykPbnLoYXLHfb73WrpJHDKo3Y00zpfZn5Xa"
              client-reference-id={currentUser?.id}
              customer-email={currentUser?.email}
            >
            </stripe-pricing-table>
          </div>

          {/* Security Info */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Secured by Stripe</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>Cancel anytime</span>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-3">
              Enter your card to start your free trial. You'll be charged after the trial ends. Cancel anytime before then.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallPopup;
